import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Container, SectionHeader } from "@/components/Section";
import { api, resolveImg } from "@/lib/api";

const CATEGORIES = ["All", "Pre-Primary & Primary", "School Students", "Classroom Learning", "Commerce & Degree", "Celebrations", "PTG Family"];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => { api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => cat === "All" ? items : items.filter((i) => i.category === cat), [items, cat]);
  const openAt = (idx) => setLightbox(idx);
  const nav = (dir) => setLightbox((p) => (p + dir + filtered.length) % filtered.length);

  return (
    <div data-testid="gallery-page">
      <Seo title="Celebrations & Gallery" description="Photos from PTG celebrations, festivals, student activities, result felicitations and classroom life." />
      <section className="bg-blue-950 py-16 sm:py-20 text-white">
        <Container>
          <SectionHeader center={false} light eyebrow="Celebrations & Gallery" title="Life at PTG in pictures"
            subtitle="Real moments from the Prasad Tutor Group family. New photographs are added by the PTG team." />
        </Container>
      </section>

      <section className="pt-16 sm:pt-24 pb-10">
        <Container>
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${cat === c ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="columns-2 md:columns-3 lg:columns-3 gap-4 space-y-4 [column-fill:balance]">
            {filtered.map((g, i) => (
              <Reveal key={g.id} delay={(i % 8) * 0.04}>
                <button onClick={() => openAt(i)} className="block w-full break-inside-avoid group relative overflow-hidden rounded-xl" data-testid={`gallery-item-${i}`}>
                  <img src={resolveImg(g.image)} alt={g.caption || "Prasad Tutor Group, Khar East, Mumbai"} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <p className="text-white text-sm text-left font-medium">{g.caption}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-center text-slate-500 flex flex-col items-center gap-3"><ImageIcon className="h-10 w-10 text-slate-300" />No photos in this category yet.</p>}
        </Container>
      </section>

      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl bg-slate-950 border-slate-800 p-2 sm:p-4" data-testid="gallery-lightbox">
          {lightbox !== null && filtered[lightbox] && (
            <div className="relative">
              <img src={resolveImg(filtered[lightbox].image)} alt={filtered[lightbox].caption || "Prasad Tutor Group, Khar East, Mumbai"} className="w-full max-h-[75vh] object-contain rounded-lg" />
              <p className="text-center text-slate-200 mt-3 text-sm">{filtered[lightbox].caption}</p>
              {filtered.length > 1 && (
                <>
                  <button onClick={() => nav(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-2 text-white hover:bg-white/40" data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
                  <button onClick={() => nav(1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-2 text-white hover:bg-white/40" data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
